import React from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Input, DatePicker, InputNumber, Form, Radio, FormInstance, DatePickerProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';

import { useStore } from 'zustand';
import { calendarService, calendarStore } from 'entities/calendar';
import styles from './CalendarModalBody.module.scss';

const { RangePicker } = DatePicker;

interface CalendarModalBodyProps {
  form: FormInstance;
  calendarId: string;
  eventId?: string;
}

const CalendarModalBody = ({ form, calendarId, eventId }: CalendarModalBodyProps): React.ReactElement => {
  const { t } = useTranslation('Calendar');

  const selectedYear = useStore(calendarStore, (state) => state.selectedYear);

  const { data: calendar } = useSuspenseQuery(
    calendarService.queryOptions({
      id: calendarId,
      year: selectedYear.toString(),
    }),
  );

  const events = calendar?.events || [];

  const handleRangeCalendarChange = (dates: (Dayjs | null)[]): void => {
    if (!dates || !dates[0]) {
      form.setFieldValue('dateRange', []);
    } else {
      form.setFieldValue('dateRange', dates);
    }
  };

  const filteredEvents = eventId ? events.filter((event) => event.id !== eventId) : events;

  const eventRanges = filteredEvents.map((event: { startDate: string; endDate: string }) => ({
    start: dayjs(event.startDate),
    end: dayjs(event.endDate),
  }));

  const isDateInEvents = (date: Dayjs): boolean =>
    eventRanges.some(
      (range) =>
        (date.isAfter(range.start, 'day') || date.isSame(range.start, 'day')) &&
        (date.isBefore(range.end, 'day') || date.isSame(range.end, 'day')),
    );

  const hasEventInRange = (start: Dayjs, end: Dayjs): boolean => {
    return eventRanges.some(
      (range) =>
        (start.isBefore(range.end, 'day') || start.isSame(range.end, 'day')) &&
        (end.isAfter(range.start, 'day') || end.isSame(range.start, 'day')),
    );
  };

  const disabledDate: DatePickerProps['disabledDate'] = (current, { from }) => {
    const dateRange = form.getFieldValue('dateRange') || [];
    const [start, end] = dateRange;

    if (eventId && !start && !end) {
      const editingEvent = events.find((event) => event.startDate === eventId);
      if (editingEvent) {
        const originalStart = dayjs(editingEvent.startDate);
        const originalEnd = dayjs(editingEvent.endDate);
        if (
          (current.isAfter(originalStart, 'day') || current.isSame(originalStart, 'day')) &&
          (current.isBefore(originalEnd, 'day') || current.isSame(originalEnd, 'day'))
        ) {
          return false;
        }
      }
    }

    if (start && end) {
      const rangeStart = dayjs(start);
      const rangeEnd = dayjs(end);

      if (isDateInEvents(current)) return true;
      if (current.isBefore(rangeEnd, 'day') && hasEventInRange(current, rangeEnd)) return true;
      if (current.isAfter(rangeStart, 'day') && hasEventInRange(rangeStart, current)) return true;
      return false;
    }

    if (from) {
      const startDay = from as Dayjs;
      if (current.isBefore(startDay, 'day')) return true;
      if (hasEventInRange(startDay, current)) return true;
      return false;
    }

    if (end) {
      const endDay = dayjs(end);
      if (current.isAfter(endDay, 'day')) return true;
      if (hasEventInRange(current, endDay)) return true;
      if (isDateInEvents(current)) return true;
      return false;
    }

    return isDateInEvents(current);
  };

  return (
    <React.Fragment>
      <Form.Item
        label={t('calendarModal.body.eventName.label')}
        name="name"
        validateTrigger="onSubmit"
        rules={[
          { required: true, message: t('calendarModal.body.eventName.validation.text') },
          {
            validator: (_, value) => {
              if (typeof value === 'string' && value.trim().length < 3) {
                return Promise.reject(new Error(t('calendarModal.body.eventName.lengthValidation.text')));
              }
              return Promise.resolve();
            },
          },
        ]}
      >
        <Input maxLength={40} minLength={3} placeholder={t('calendarModal.body.eventName.placeholder')} />
      </Form.Item>
      <Form.Item
        label={t('calendarModal.body.dateRange.label')}
        name="dateRange"
        rules={[{ required: true, message: t('calendarModal.body.dateRange.validation.text') }]}
      >
        <RangePicker
          className={styles.rangePicker}
          disabledDate={disabledDate}
          onCalendarChange={handleRangeCalendarChange}
        />
      </Form.Item>
      <Form.Item label={t('calendarModal.body.eventType.label')} name="exclusionType">
        <Radio.Group
          block
          optionType="button"
          options={[
            { value: 'NON_WORKING_DAY', label: t('calendarModal.body.eventType.nonWorking.label') },
            { value: 'WORKING_DAY', label: t('calendarModal.body.eventType.working.label') },
          ]}
        />
      </Form.Item>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) => prevValues.exclusionType !== currentValues.exclusionType}
      >
        {({ getFieldValue }) =>
          getFieldValue('exclusionType') === 'WORKING_DAY' ? (
            <Form.Item
              name="workingHours"
              label={t('calendarModal.body.hours.label')}
              rules={[
                {
                  validator(_, value) {
                    if (getFieldValue('exclusionType') === 'WORKING_DAY') {
                      if (value === undefined || value < 1 || value > 8) {
                        return Promise.reject(new Error(t('calendarModal.body.hours.validation.text')));
                      }
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber min={1} max={8} className={styles.numberInput} />
            </Form.Item>
          ) : (
            <Form.Item name="workingHours" label={t('calendarModal.body.hours.label')}>
              <InputNumber disabled className={styles.numberInput} />
            </Form.Item>
          )
        }
      </Form.Item>
    </React.Fragment>
  );
};

export { CalendarModalBody };
